import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Tab, Nav } from 'react-bootstrap';
import { FilterList } from '@mui/icons-material';
import './TelemetricDashboard.css';

// Import components
import SensorMetricsCards from './components/SensorMetricsCards';
import TelematicsErrorChart from './components/TelematicsErrorChart';
import FaultCodesChart from './components/FaultCodesChart';
import ErrorCodesChart from './components/ErrorCodesChart';
import MetricsCards from './components/MetricsCards';
import MessagesTrendChart from './components/MessagesTrendChart';
import FotaChart from './components/FotaChart';
import VncTrendChart from './components/VncTrendChart';
import ManufacturerSwitcher from './components/ManufacturerSwitcher';
import ChartFilters from './components/ChartFilters';

import { useChartFilters } from './hooks/useChartFilters';

function TelemetricDashboard() {
	const [activeTab, setActiveTab] = useState('baseTelemetry');
	const [selectedManufacturer, setSelectedManufacturer] = useState('toyota');
	const [showFilterPanel, setShowFilterPanel] = useState(false);

	// Define the order of manufacturers for cycling
	const manufacturerOrder = ['toyota', 'raymond-us', 'raymond-eu'];

	// Initialize chart filters
	const { chartFilters, handleFilterChange, handleResetFilters } = useChartFilters();

	return (
		<Container fluid className="mt-3 mb-4">
			<Row>
				<Col>
					{/* React Bootstrap Tab.Container with Custom Layout */}
					<Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
						{/* Custom Tab Navigation with Manufacturer Switcher and Filter Button */}
						<div className="d-flex align-items-center justify-content-between border-bottom mb-3">
							<Nav variant="tabs" className="border-0">
								<Nav.Item>
									<Nav.Link eventKey="baseTelemetry">Telemetry Snapshot</Nav.Link>
								</Nav.Item>
								<Nav.Item>
									<Nav.Link eventKey="sensorBaseline">Sensor Snapshot & Errors</Nav.Link>
								</Nav.Item>
							</Nav>

							{/* Manufacturer Switcher and Filter Button */}
							<div className="d-flex align-items-center gap-3">
								<ManufacturerSwitcher
									selectedManufacturer={selectedManufacturer}
									onManufacturerChange={setSelectedManufacturer}
									manufacturerOrder={manufacturerOrder}
								/>

								{/* Filter Button */}
								<Button
									variant={showFilterPanel ? "primary" : "outline-primary"}
									size="sm"
									onClick={() => setShowFilterPanel(!showFilterPanel)}
									className="d-flex align-items-center"
									title={showFilterPanel ? "Hide filters" : "Show filters"}
								>
									<FilterList style={{ fontSize: '16px', marginRight: '4px' }} />
									Filters
								</Button>
							</div>
						</div>

						{/* Tab Content */}
						<Tab.Content>
							<Tab.Pane eventKey="baseTelemetry">
								{/* IOT Hub Metrics - Full Width */}
								<MetricsCards selectedManufacturer={selectedManufacturer} />

								{/* Tab Content with Filter Panel */}
								<Row>
									<Col md={showFilterPanel ? 9 : 12}>
										<MessagesTrendChart chartFilters={chartFilters} selectedManufacturer={selectedManufacturer} />
										<Row className="mt-4">
											<Col md={6}>
												<FotaChart selectedManufacturer={selectedManufacturer} />
											</Col>
											<Col md={6}>
												<VncTrendChart chartFilters={chartFilters} selectedManufacturer={selectedManufacturer} />
											</Col>
										</Row>
									</Col>
									{/* Inline Filter Panel */}
									{showFilterPanel && (
										<Col md={3} className="filter-panel">
											<div className="p-3">
												<ChartFilters
													chartFilters={chartFilters}
													handleFilterChange={handleFilterChange}
													handleResetFilters={handleResetFilters}
													selectedManufacturer={selectedManufacturer}
												/>
											</div>
										</Col>
									)}
								</Row>
							</Tab.Pane>
							<Tab.Pane eventKey="sensorBaseline">
								{/* Tab Content with Filter Panel */}
								<Row>
									<Col md={showFilterPanel ? 9 : 12}>
										{/* Sensor Metrics - Full Width */}
										<SensorMetricsCards selectedManufacturer={selectedManufacturer} chartFilters={chartFilters} />

										{/* Conditional rendering based on manufacturer */}
										{selectedManufacturer === 'toyota' ? (
											<TelematicsErrorChart chartFilters={chartFilters} />
										) : (
											<>
												<FaultCodesChart chartFilters={chartFilters} selectedManufacturer={selectedManufacturer} />
												<ErrorCodesChart chartFilters={chartFilters} selectedManufacturer={selectedManufacturer} />
											</>
										)}
									</Col>
									{/* Inline Filter Panel */}
									{showFilterPanel && (
										<Col md={3} className="filter-panel">
											<div className="p-3">
												<ChartFilters
													chartFilters={chartFilters}
													handleFilterChange={handleFilterChange}
													handleResetFilters={handleResetFilters}
													selectedManufacturer={selectedManufacturer}
												/>
											</div>
										</Col>
									)}
								</Row>
							</Tab.Pane>
						</Tab.Content>
					</Tab.Container>
				</Col>
			</Row>
		</Container>
	);
}

export default TelemetricDashboard;
